import { useCallback, useEffect, useState } from "react";
import {
  type ClienteNexo,
  type DecisionRevision,
  type DocumentoJusticiaDetalle,
  type PropuestaJusticia,
  justiciaDocumento,
  justiciaListarPropuestas,
  justiciaRevisarAfirmacion,
  justiciaRevisarResolucion,
} from "../api.ts";
import { C, fecha, st } from "../theme.ts";
import { Cargando, ErrorBox, celdaTd, celdaTh } from "../ui/componentes.tsx";

const FUENTE_ES: Record<string, string> = {
  us_doj_news: "Department of Justice",
  us_fbi_wanted: "FBI Wanted",
  us_federal_register: "Federal Register",
  us_dea_fugitives: "DEA Fugitives",
  us_state_narcotics_rewards: "Department of State",
};

const POSTURA_ES: Record<string, string> = {
  government_allegation: "Alegación de autoridad",
  defendant_admission: "Admisión de la parte",
  jury_verdict: "Veredicto de jurado",
  court_finding: "Determinación judicial",
  court_order: "Orden judicial",
  unknown: "Postura por determinar",
  alegacion: "Alegación de autoridad",
};

function etiquetaFuente(id: string): string {
  return FUENTE_ES[id] ?? id;
}

function etiquetaPredicate(value: string): string {
  return value.replace(/_/g, " ");
}

function locatorTexto(locator: Record<string, unknown> | null): string {
  if (!locator) return "Sin localizador estructurado";
  const field = typeof locator.field === "string" ? locator.field : null;
  const paragraph = typeof locator.paragraph === "number" ? locator.paragraph + 1 : null;
  const start = typeof locator.start === "number"
    ? locator.start
    : typeof locator.charStart === "number"
    ? locator.charStart
    : null;
  const end = typeof locator.end === "number"
    ? locator.end
    : typeof locator.charEnd === "number"
    ? locator.charEnd
    : null;
  const parts = [
    field ? `campo ${field}` : null,
    paragraph ? `párrafo ${String(paragraph)}` : null,
    start !== null && end !== null ? `caracteres ${String(start)}–${String(end)}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : JSON.stringify(locator);
}

interface RevisionActiva {
  tipo: "afirmacion" | "resolucion";
  id: string;
  decision: DecisionRevision;
  notas: string;
}

/** Cola de revisión humana de afirmaciones documentales de Nexo Justicia. */
export function Justicia({ cliente }: { cliente: ClienteNexo }) {
  const [propuestas, setPropuestas] = useState<PropuestaJusticia[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState<RevisionActiva | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [detalle, setDetalle] = useState<DocumentoJusticiaDetalle | null | undefined>(undefined);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    setPropuestas(undefined);
    justiciaListarPropuestas(cliente)
      .then(setPropuestas)
      .catch((e: unknown) => {
        setPropuestas([]);
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [cliente]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirDocumento = useCallback((id: string) => {
    if (detalleId === id) {
      setDetalleId(null);
      setDetalle(undefined);
      setErrorDetalle(null);
      return;
    }
    setDetalleId(id);
    setDetalle(undefined);
    setErrorDetalle(null);
    justiciaDocumento(cliente, id)
      .then(setDetalle)
      .catch((e: unknown) => {
        setDetalle(null);
        setErrorDetalle(e instanceof Error ? e.message : String(e));
      });
  }, [cliente, detalleId]);

  async function confirmarRevision(): Promise<void> {
    if (!revision) return;
    setGuardando(true);
    setError(null);
    try {
      if (revision.tipo === "afirmacion") {
        await justiciaRevisarAfirmacion(
          cliente,
          revision.id,
          revision.decision,
          revision.notas,
        );
        setPropuestas((actual) => actual?.filter((p) => p.id !== revision.id));
      } else {
        await justiciaRevisarResolucion(
          cliente,
          Number(revision.id),
          revision.decision,
          revision.notas,
        );
        setDetalle((actual) => actual
          ? {
            ...actual,
            mentions: actual.mentions.map((m) => ({
              ...m,
              resolutions: m.resolutions.map((r) =>
                String(r.id) === revision.id ? { ...r, review_status: revision.decision } : r
              ),
            })),
          }
          : actual);
      }
      setRevision(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div>
      <div style={st.tarjeta}>
        <h1 style={{ ...st.display, fontSize: 22, margin: "0 0 4px" }}>Justicia</h1>
        <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.55 }}>
          Revisión de afirmaciones extraídas de documentos oficiales. Una propuesta sólo dice que
          la fuente publicó el fragmento mostrado; aprobarla confirma su trazabilidad, no la
          culpabilidad de una persona ni la veracidad de una alegación.
        </p>
      </div>

      {error ? (
        <div>
          <ErrorBox mensaje={`La capa Justicia no está disponible: ${error}`} />
          <p style={{ fontSize: 12, color: C.muted, margin: "-5px 0 12px" }}>
            Esta falla no afecta el Explorador, Documentos ni Cruce de listas.
          </p>
        </div>
      ) : null}

      {propuestas === undefined ? <Cargando texto="Cargando propuestas de revisión…" /> : (
        <div style={st.tarjeta}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <p style={st.eyebrow}>Cola asignada ({String(propuestas.length)})</p>
            <button type="button" style={st.btn} onClick={cargar}>Actualizar</button>
          </div>
          {propuestas.length === 0 ? (
            <p style={{ fontSize: 13, color: C.muted }}>
              No hay propuestas disponibles para este revisor.
            </p>
          ) : propuestas.map((p) => {
            const activa = revision?.tipo === "afirmacion" && revision.id === p.id;
            return (
              <div
                key={p.id}
                style={{ borderTop: `1px solid ${C.line}`, padding: "13px 0" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 260, flex: 1 }}>
                    <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 4 }}>
                      {etiquetaFuente(p.fuente_id)} · {POSTURA_ES[p.postura] ?? p.postura} · propuesta {fecha(p.created_at)}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{p.titulo}</div>
                    <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>
                      {p.subject_text ?? "Persona o empresa por determinar"} · {etiquetaPredicate(p.predicate)}
                      {p.object_text ? ` · ${p.object_text}` : ""}
                    </div>
                  </div>
                  <button type="button" style={st.btn} onClick={() => abrirDocumento(p.documento_id)}>
                    {detalleId === p.documento_id ? "Ocultar documento" : "Ver documento"}
                  </button>
                </div>
                <blockquote
                  style={{
                    margin: "10px 0 5px",
                    padding: "9px 12px",
                    borderLeft: `3px solid ${C.tiel}`,
                    background: C.tielSoft,
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: C.ink,
                  }}
                >
                  “{p.evidence_excerpt}”
                </blockquote>
                <div style={{ fontSize: 11.5, color: C.muted }}>
                  {locatorTexto(p.evidence_locator)}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    style={{ ...st.btn, color: C.green }}
                    onClick={() => setRevision({ tipo: "afirmacion", id: p.id, decision: "approved", notas: "" })}
                  >
                    Aprobar evidencia
                  </button>
                  <button
                    type="button"
                    style={{ ...st.btn, color: C.red }}
                    onClick={() => setRevision({ tipo: "afirmacion", id: p.id, decision: "rejected", notas: "" })}
                  >
                    Descartar propuesta
                  </button>
                </div>
                {activa ? (
                  <PanelConfirmacion
                    revision={revision}
                    guardando={guardando}
                    onCambiarNotas={(notas) => setRevision({ ...revision, notas })}
                    onCancelar={() => setRevision(null)}
                    onConfirmar={() => void confirmarRevision()}
                  />
                ) : null}
                {detalleId === p.documento_id ? (
                  <DetalleDocumentoJusticia
                    detalle={detalle}
                    error={errorDetalle}
                    revision={revision}
                    guardando={guardando}
                    onRevisar={(id, decision) =>
                      setRevision({ tipo: "resolucion", id: String(id), decision, notas: "" })}
                    onCambiarNotas={(notas) => revision && setRevision({ ...revision, notas })}
                    onCancelar={() => setRevision(null)}
                    onConfirmar={() => void confirmarRevision()}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PanelConfirmacion({
  revision,
  guardando,
  onCambiarNotas,
  onCancelar,
  onConfirmar,
}: {
  revision: RevisionActiva;
  guardando: boolean;
  onCambiarNotas: (notas: string) => void;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  const aprobar = revision.decision === "approved";
  return (
    <div style={{ marginTop: 10, padding: 12, borderRadius: 10, background: aprobar ? C.greenBg : C.redBg }}>
      <label style={st.label} htmlFor={`notas-${revision.tipo}-${revision.id}`}>
        {aprobar ? "Confirmar aprobación" : "Confirmar descarte"} · notas opcionales
      </label>
      <textarea
        id={`notas-${revision.tipo}-${revision.id}`}
        value={revision.notas}
        onChange={(e) => onCambiarNotas(e.target.value)}
        rows={2}
        style={{ ...st.input, width: "100%", resize: "vertical" }}
        placeholder="Razón o contexto de la revisión…"
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button type="button" style={st.btnPrim} disabled={guardando} onClick={onConfirmar}>
          {guardando ? "Guardando…" : aprobar ? "Confirmar aprobación" : "Confirmar descarte"}
        </button>
        <button type="button" style={st.btn} disabled={guardando} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

function DetalleDocumentoJusticia({
  detalle,
  error,
  revision,
  guardando,
  onRevisar,
  onCambiarNotas,
  onCancelar,
  onConfirmar,
}: {
  detalle: DocumentoJusticiaDetalle | null | undefined;
  error: string | null;
  revision: RevisionActiva | null;
  guardando: boolean;
  onRevisar: (id: number, decision: DecisionRevision) => void;
  onCambiarNotas: (notas: string) => void;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  if (error) {
    return <ErrorBox mensaje={`No se pudo abrir el detalle documental: ${error}`} />;
  }
  if (detalle === undefined) return <Cargando texto="Cargando evidencia documental…" />;
  if (detalle === null) return <p style={{ fontSize: 12.5, color: C.muted }}>Detalle documental no disponible.</p>;
  const d = detalle.documento;
  return (
    <div style={{ marginTop: 12, padding: 12, border: `1px solid ${C.line}`, borderRadius: 10 }}>
      <div style={{ fontSize: 12.5 }}>
        <strong>{etiquetaFuente(d.fuente_id)}</strong> · {d.type} · {fecha(d.published_at)}
        {d.canonical_url ? (
          <> · <a href={d.canonical_url} target="_blank" rel="noreferrer" style={{ color: C.tiel }}>Fuente original ↗</a></>
        ) : null}
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>
        {detalle.version.payload_sha256 ? (
          <span title={detalle.version.payload_sha256} style={{ fontFamily: "ui-monospace, Consolas, monospace" }}>
            SHA-256 {detalle.version.payload_sha256.slice(0, 16)}…
          </span>
        ) : "Hash no disponible en esta respuesta"}
        {detalle.version.parser_version ? ` · parser ${detalle.version.parser_version}` : ""}
      </div>
      {detalle.assertions.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <p style={st.eyebrow}>Afirmaciones del documento ({String(detalle.assertions.length)})</p>
          {detalle.assertions.map((a) => (
            <div key={a.id} style={{ fontSize: 12.5, marginBottom: 8 }}>
              <strong>{etiquetaPredicate(a.predicate)}</strong> · {POSTURA_ES[a.posture] ?? a.posture} · {a.review_status}
              <div style={{ color: C.muted, marginTop: 2 }}>“{a.evidence_excerpt}”</div>
              <div style={{ color: C.muted, fontSize: 11 }}>{locatorTexto(a.evidence_locator)}</div>
            </div>
          ))}
        </div>
      ) : null}
      {detalle.mentions.some((m) => m.resolutions.length > 0) ? (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
          <thead><tr><th style={celdaTh}>Mención</th><th style={celdaTh}>Candidato</th><th style={celdaTh}>Estado</th><th style={celdaTh}>Revisión</th></tr></thead>
          <tbody>
            {detalle.mentions.flatMap((mention) => mention.resolutions.map((r) => {
              const activa = revision?.tipo === "resolucion" && revision.id === String(r.id);
              return (
                <tr key={r.id}>
                  <td style={celdaTd}>
                    {mention.mention_text}
                    <div style={{ color: C.muted, fontSize: 11 }}>{mention.proposed_role ?? mention.mention_type}</div>
                  </td>
                  <td style={celdaTd}>
                    {r.entity_id ?? "Sin entidad resuelta"}
                    <div style={{ color: C.muted, fontSize: 11 }}>{r.method} {r.score !== null && r.score !== undefined ? `· ${String(r.score)}` : ""}</div>
                  </td>
                  <td style={celdaTd}>{r.review_status}</td>
                  <td style={celdaTd}>
                    {r.review_status === "proposed" ? (
                      <>
                        <button type="button" style={{ ...st.btn, padding: "5px 8px", color: C.green }} onClick={() => onRevisar(r.id, "approved")}>Aprobar</button>{" "}
                        <button type="button" style={{ ...st.btn, padding: "5px 8px", color: C.red }} onClick={() => onRevisar(r.id, "rejected")}>Descartar</button>
                        {activa && revision ? (
                          <PanelConfirmacion
                            revision={revision}
                            guardando={guardando}
                            onCambiarNotas={onCambiarNotas}
                            onCancelar={onCancelar}
                            onConfirmar={onConfirmar}
                          />
                        ) : null}
                      </>
                    ) : "Revisada"}
                  </td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
